/**
 * Predicates for requirements that turn on a single confirmed fact.
 *
 * They share a file because each is three lines and one-file-per-predicate
 * would obscure rather than clarify; every one is still an individually
 * exported pure function with its own boundary tests (§10.4). Anything with
 * real logic — dates, thresholds, stage variation — lives in its own file.
 *
 * Every one FAILS CLOSED: `null` means unknown, which is PENDING_EVIDENCE,
 * never an assumed MET (§8.5).
 */
import type { CaseFacts } from "../types.js";
import type { EvaluatePredicate, PredicateOutcome } from "./predicate.js";

/** Unknown → PENDING_EVIDENCE; false → NOT_MET; true → MET. */
export function fromFlag(value: boolean | null, pendingNote: string, failNote?: string): PredicateOutcome {
  if (value === null) return { status: "PENDING_EVIDENCE", notes: [pendingNote] };
  if (value) return { status: "MET" };
  return { status: "NOT_MET", ...(failNote ? { notes: [failNote] } : {}) };
}

const flagPredicate =
  (read: (facts: CaseFacts) => boolean | null, pending: string, fail?: string): EvaluatePredicate =>
  (facts) =>
    fromFlag(read(facts), pending, fail);

// ── relationship ──────────────────────────────────────────────────────────
export const marriage_legally_valid = flagPredicate(
  (f) => f.relationship.marriage_legally_valid,
  "Whether the marriage or civil partnership is recognised as valid in the country where it took place is needed.",
);

export const not_within_prohibited_degrees = flagPredicate(
  // The fact records whether they ARE within the prohibited degrees, so it inverts.
  (f) =>
    f.relationship.within_prohibited_degrees === null ? null : !f.relationship.within_prohibited_degrees,
  "Whether the parties are related within the prohibited degrees of relationship is needed.",
);

export const met_in_person = flagPredicate(
  (f) => f.relationship.met_in_person,
  "Whether the couple have met in person is needed.",
);

export const previous_relationships_ended = flagPredicate(
  (f) => f.relationship.previous_relationships_ended,
  "Whether any previous relationship has permanently broken down is needed.",
);

// ── accommodation ─────────────────────────────────────────────────────────
export const accommodation_adequate = flagPredicate(
  (f) => f.accommodation.adequate,
  "Whether the accommodation is adequate for the family without overcrowding is needed.",
);

export const legal_right_to_occupy = flagPredicate(
  (f) => f.accommodation.legal_right_to_occupy,
  "Whether there is a legal right to occupy the accommodation is needed.",
);

export const without_public_funds = flagPredicate(
  (f) => f.accommodation.without_public_funds,
  "Whether the accommodation can be maintained without recourse to public funds is needed.",
);

// ── financial hygiene ─────────────────────────────────────────────────────
export const figures_are_gross = flagPredicate(
  (f) => f.finances.figures_are_gross,
  "Whether the income figures given are gross — before tax — is needed.",
);

export const savings_lawful_and_controlled: EvaluatePredicate = (facts) => {
  const savings = facts.finances.cash_savings;
  if (savings === null) {
    return { status: "PENDING_EVIDENCE", notes: ["Details of the savings held are needed."] };
  }
  if (savings.lawful_source === null || savings.under_own_control === null) {
    return {
      status: "PENDING_EVIDENCE",
      notes: [
        "The rules ask that the savings are from a lawful source and under the control of the applicant, their partner, or both jointly.",
      ],
    };
  }
  if (savings.lawful_source && savings.under_own_control) return { status: "MET" };
  return {
    status: "NOT_MET",
    notes: ["The rules ask that the savings are from a lawful source and under their own control."],
  };
};
